import React from "react";
import { Box, Grid, makeStyles, createStyles } from "@material-ui/core";
import Skeleton from "@material-ui/lab/Skeleton";
import withAuthority from "../../../../../components/Auth/withAuthority";
import Authorities from "../../../../../auth/authorities";
import { CustomTheme } from "../../../../../types/customTheme";

const useStyles = makeStyles((theme: CustomTheme) =>
  createStyles({
    container: {
      padding: "24px",
      backgroundColor: theme.palette.background.default,
      minHeight: "100vh",
    },
    breadcrumbSkeleton: {
      display: "flex",
      alignItems: "center",
      gap: "8px",
      marginBottom: "24px",
      padding: "12px 0",
    },
    backButtonSkeleton: {
      marginBottom: "16px",
    },
    cardContainer: {
      backgroundColor: theme.palette.background.entity_background,
      borderRadius: "12px",
      padding: "24px",
      marginTop: "16px",
    },
    titleSection: {
      marginBottom: "24px",
    },
    switchRow: {
      display: "flex",
      gap: "16px",
      marginBottom: "24px",
      flexWrap: "wrap",
    },
    switchSkeleton: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      gap: "8px",
      minWidth: "80px",
    },
    formRow: {
      display: "flex",
      gap: "16px",
      marginBottom: "16px",
      flexWrap: "wrap",
    },
    formField: {
      flex: 1,
      minWidth: "200px",
    },
    colorSection: {
      display: "flex",
      gap: "16px",
      marginBottom: "16px",
    },
    buttonRow: {
      display: "flex",
      justifyContent: "flex-end",
      gap: "8px",
      marginTop: "24px",
    },
  })
);

const ProductSkeletonLoading = () => {
  const classes = useStyles();

  return (
    <Box className={classes.container}>
      {/* Breadcrumb Skeleton */}
      <Box className={classes.breadcrumbSkeleton}>
        <Skeleton variant="text" width={80} height={24} style={{ borderRadius: 4 }} />
        <Skeleton variant="text" width={16} height={24} style={{ borderRadius: 4 }} />
        <Skeleton variant="text" width={100} height={24} style={{ borderRadius: 4 }} />
        <Skeleton variant="text" width={16} height={24} style={{ borderRadius: 4 }} />
        <Skeleton variant="text" width={120} height={24} style={{ borderRadius: 4 }} />
      </Box>

      {/* Back Button Skeleton */}
      <Box className={classes.backButtonSkeleton}>
        <Skeleton variant="rect" width={120} height={36} style={{ borderRadius: 8 }} />
      </Box>

      {/* Card Container */}
      <Box className={classes.cardContainer}>
        {/* Title Skeleton */}
        <Box className={classes.titleSection}>
          <Skeleton variant="text" width={250} height={32} style={{ borderRadius: 4 }} />
        </Box>

        {/* Switch Row Skeletons */}
        <Box className={classes.switchRow}>
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <Box key={i} className={classes.switchSkeleton}>
              <Skeleton variant="circle" width={40} height={40} />
              <Skeleton variant="text" width={60} height={16} style={{ borderRadius: 4 }} />
            </Box>
          ))}
        </Box>

        {/* Form Fields Skeletons */}
        <Grid container spacing={2}>
          {/* Row 1 */}
          <Grid item xs={12} sm={6}>
            <Box className={classes.formField}>
              <Skeleton variant="text" width={80} height={20} style={{ marginBottom: 8, borderRadius: 4 }} />
              <Skeleton variant="rect" width="100%" height={56} style={{ borderRadius: 8 }} />
            </Box>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Box className={classes.formField}>
              <Skeleton variant="text" width={80} height={20} style={{ marginBottom: 8, borderRadius: 4 }} />
              <Skeleton variant="rect" width="100%" height={56} style={{ borderRadius: 8 }} />
            </Box>
          </Grid>

          {/* Row 2 */}
          <Grid item xs={12} sm={6}>
            <Box className={classes.formField}>
              <Skeleton variant="text" width={80} height={20} style={{ marginBottom: 8, borderRadius: 4 }} />
              <Skeleton variant="rect" width="100%" height={56} style={{ borderRadius: 8 }} />
            </Box>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Box className={classes.formField}>
              <Skeleton variant="text" width={100} height={20} style={{ marginBottom: 8, borderRadius: 4 }} />
              <Skeleton variant="rect" width="100%" height={56} style={{ borderRadius: 8 }} />
            </Box>
          </Grid>

          {/* Row 3 */}
          <Grid item xs={12} sm={6}>
            <Box className={classes.formField}>
              <Skeleton variant="text" width={60} height={20} style={{ marginBottom: 8, borderRadius: 4 }} />
              <Skeleton variant="rect" width="100%" height={56} style={{ borderRadius: 8 }} />
            </Box>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Box className={classes.formField}>
              <Skeleton variant="text" width={60} height={20} style={{ marginBottom: 8, borderRadius: 4 }} />
              <Skeleton variant="rect" width="100%" height={56} style={{ borderRadius: 8 }} />
            </Box>
          </Grid>

          {/* Description Field (Full Width) */}
          <Grid item xs={12}>
            <Box className={classes.formField}>
              <Skeleton variant="text" width={100} height={20} style={{ marginBottom: 8, borderRadius: 4 }} />
              <Skeleton variant="rect" width="100%" height={56} style={{ borderRadius: 8 }} />
            </Box>
          </Grid>

          {/* Row 4 */}
          <Grid item xs={12} sm={6}>
            <Box className={classes.formField}>
              <Skeleton variant="text" width={100} height={20} style={{ marginBottom: 8, borderRadius: 4 }} />
              <Skeleton variant="rect" width="100%" height={56} style={{ borderRadius: 8 }} />
            </Box>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Box className={classes.formField}>
              <Skeleton variant="text" width={100} height={20} style={{ marginBottom: 8, borderRadius: 4 }} />
              <Skeleton variant="rect" width="100%" height={56} style={{ borderRadius: 8 }} />
            </Box>
          </Grid>

          {/* Row 5 */}
          <Grid item xs={12} sm={6}>
            <Box className={classes.formField}>
              <Skeleton variant="text" width={120} height={20} style={{ marginBottom: 8, borderRadius: 4 }} />
              <Skeleton variant="rect" width="100%" height={56} style={{ borderRadius: 8 }} />
            </Box>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Box className={classes.formField}>
              <Skeleton variant="text" width={80} height={20} style={{ marginBottom: 8, borderRadius: 4 }} />
              <Skeleton variant="rect" width="100%" height={56} style={{ borderRadius: 8 }} />
            </Box>
          </Grid>

          {/* Row 6 */}
          <Grid item xs={12} sm={6}>
            <Box className={classes.formField}>
              <Skeleton variant="text" width={80} height={20} style={{ marginBottom: 8, borderRadius: 4 }} />
              <Skeleton variant="rect" width="100%" height={56} style={{ borderRadius: 8 }} />
            </Box>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Box className={classes.formField}>
              <Skeleton variant="text" width={80} height={20} style={{ marginBottom: 8, borderRadius: 4 }} />
              <Skeleton variant="rect" width="100%" height={56} style={{ borderRadius: 8 }} />
            </Box>
          </Grid>

          {/* Row 7 - Item Type and Name Short */}
          <Grid item xs={12} sm={6}>
            <Box className={classes.formField}>
              <Skeleton variant="text" width={80} height={20} style={{ marginBottom: 8, borderRadius: 4 }} />
              <Skeleton variant="rect" width="100%" height={56} style={{ borderRadius: 8 }} />
            </Box>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Box className={classes.formField}>
              <Skeleton variant="text" width={100} height={20} style={{ marginBottom: 8, borderRadius: 4 }} />
              <Skeleton variant="rect" width="100%" height={56} style={{ borderRadius: 8 }} />
            </Box>
          </Grid>

          {/* Row 8 - Img URL */}
          <Grid item xs={12}>
            <Box className={classes.formField}>
              <Skeleton variant="text" width={80} height={20} style={{ marginBottom: 8, borderRadius: 4 }} />
              <Skeleton variant="rect" width="100%" height={56} style={{ borderRadius: 8 }} />
            </Box>
          </Grid>

          {/* Color Selection Section */}
          <Grid item xs={12}>
            <Box className={classes.colorSection}>
              <Box style={{ flex: 1 }}>
                <Skeleton variant="text" width={60} height={20} style={{ marginBottom: 8, borderRadius: 4 }} />
                <Skeleton variant="rect" width="100%" height={56} style={{ borderRadius: 8 }} />
              </Box>
              <Box style={{ width: 80 }}>
                <Skeleton variant="circle" width={56} height={56} />
              </Box>
            </Box>
          </Grid>
        </Grid>

        {/* Buttons Row */}
        <Box className={classes.buttonRow}>
          <Skeleton variant="rect" width={100} height={36} style={{ borderRadius: 8 }} />
          <Skeleton variant="rect" width={100} height={36} style={{ borderRadius: 8 }} />
        </Box>
      </Box>
    </Box>
  );
};

export default withAuthority(ProductSkeletonLoading, Authorities.DASHBOARD_READ);